import { useMemo, useState } from 'react';
import styles from '../../routes/analytics/Analytics.module.css';

/** One usage bucket's concurrent sessions, from /api/analytics or /api/admin/reports with concurrency=1. */
export interface ConcurrencyPoint {
  timestamp: number;
  /** Most sessions live in any one minute of the bucket. */
  peak: number;
  /** 90th percentile of per-minute sessions over all the bucket's minutes, quiet ones included. */
  p90: number;
  /** Time-weighted mean concurrency: session-minutes / minutes in the bucket. */
  mean: number;
  /** Minutes of the bucket with at least one session. */
  activeMinutes: number;
  /** Minutes of the bucket observed so far. */
  minutes: number;
}

export interface ConcurrencyChartProps {
  data: ConcurrencyPoint[];
  /** Formats a bucket start for the x axis and tooltip (each page has its own range-aware formatter). */
  formatTime: (timestamp: number) => string;
  width?: number;
  height?: number;
}

const sessionsLabel = (n: number) => `${n} ${n === 1 ? 'session' : 'sessions'}`;
// Means are fractional and often small on long, mostly-quiet intervals.
const meanLabel = (n: number) => `${n === 0 ? '0' : n < 1 ? n.toFixed(2) : n.toFixed(1)} sessions`;

/**
 * Concurrent sessions per bucket as step lines over all of each interval's
 * minutes: the time-weighted mean as the primary line, P90 as a thin line, and
 * the peak as a shaded envelope from zero. The tooltip adds how much of the
 * interval was active. One flat step per bucket, nothing interpolated between
 * buckets. Totals only: percentiles don't stack across groups.
 */
export function ConcurrencyChart({ data, formatTime, width = 800, height = 300 }: ConcurrencyChartProps) {
  const [hovered, setHovered] = useState<number | null>(null);

  const padding = { top: 20, right: 40, bottom: 40, left: 60 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;

  const maxPeak = useMemo(() => Math.max(1, ...data.map(d => d.peak)), [data]);

  if (data.length === 0) {
    return (
      <svg viewBox={`0 0 ${width} ${height}`} className={styles.chart} preserveAspectRatio="xMidYMid meet">
        <text x={width / 2} y={height / 2} textAnchor="middle" className={styles.emptyMessage}>
          No usage data for this period
        </text>
      </svg>
    );
  }

  const bucketMs = data.length >= 2 ? data[1].timestamp - data[0].timestamp : 0;
  const bucketLabel = bucketMs >= 86400000 ? `${Math.round(bucketMs / 86400000)}d`
    : bucketMs >= 3600000 ? `${Math.round(bucketMs / 3600000)}h`
    : bucketMs >= 60000 ? `${Math.round(bucketMs / 60000)}m`
    : '';

  const slot = chartWidth / data.length;
  const xAt = (i: number) => padding.left + i * slot;
  const scaleY = (val: number) => padding.top + chartHeight - (val / maxPeak) * chartHeight;

  // Step path across every bucket for one statistic (left to right).
  const steps = (pick: (d: ConcurrencyPoint) => number) =>
    data.map((d, i) => `${i === 0 ? 'M' : 'L'}${xAt(i)},${scaleY(pick(d))} L${xAt(i + 1)},${scaleY(pick(d))}`).join(' ');
  const baseline = scaleY(0);

  // Round tick step (1, 2, 5, 10, 20, 50, ...) for ~5 gridlines.
  const rawStep = maxPeak / 5;
  const magnitude = 10 ** Math.floor(Math.log10(Math.max(1, rawStep)));
  const yStep = Math.max(1, [1, 2, 5, 10].map(m => m * magnitude).find(s => s >= rawStep) ?? 10 * magnitude);
  const yTicks: number[] = [];
  for (let v = 0; v <= maxPeak; v += yStep) yTicks.push(v);

  const xLabelCount = Math.min(6, data.length);
  const xLabelStep = Math.max(1, Math.floor(data.length / xLabelCount));

  return (
    <div>
      <svg viewBox={`0 0 ${width} ${height}`} className={styles.chart} preserveAspectRatio="xMidYMid meet" onMouseLeave={() => setHovered(null)}>
        {/* Grid */}
        <g className={styles.gridLines}>
          {yTicks.map((tick, i) => <line key={i} x1={padding.left} y1={scaleY(tick)} x2={width - padding.right} y2={scaleY(tick)} />)}
        </g>
        {/* Y axis */}
        <g className={styles.axis}>
          <line x1={padding.left} y1={padding.top} x2={padding.left} y2={height - padding.bottom} />
          {yTicks.map((tick, i) => <text key={i} x={padding.left - 10} y={scaleY(tick)} textAnchor="end" dominantBaseline="middle">{tick}</text>)}
          <text x={padding.left - 45} y={height / 2} textAnchor="middle" transform={`rotate(-90, ${padding.left - 45}, ${height / 2})`} className={styles.axisLabel}>SESSIONS</text>
        </g>
        {/* X axis */}
        <g className={styles.axis}>
          <line x1={padding.left} y1={height - padding.bottom} x2={width - padding.right} y2={height - padding.bottom} />
          {data.map((d, i) => {
            if (i % xLabelStep !== 0 && i !== data.length - 1) return null;
            return <text key={i} x={xAt(i) + slot / 2} y={height - padding.bottom + 20} textAnchor="middle">{formatTime(d.timestamp)}</text>;
          })}
        </g>
        {bucketLabel && (
          <text x={width - padding.right} y={padding.top - 6} textAnchor="end" className={styles.bucketLabel}>
            {bucketLabel} buckets
          </text>
        )}
        {/* Hover guide (under the lines) */}
        {hovered !== null && (
          <rect className={styles.concurrencyHover} x={xAt(hovered)} y={padding.top} width={slot} height={chartHeight} />
        )}
        {/* Peak envelope, then P90, then the mean on top */}
        <g pointerEvents="none">
          <path className={styles.concurrencyPeak} d={`${steps(d => d.peak)} L${xAt(data.length)},${baseline} L${xAt(0)},${baseline} Z`} />
          <path className={styles.concurrencyPeakEdge} d={steps(d => d.peak)} />
          <path className={styles.concurrencyP90} d={steps(d => d.p90)} />
          <path className={styles.concurrencyMean} d={steps(d => d.mean)} />
        </g>
        {/* Hit targets: the full bucket column */}
        {data.map((_, i) => (
          <rect key={i} x={xAt(i)} y={padding.top} width={slot} height={chartHeight} fill="transparent" onMouseEnter={() => setHovered(i)} />
        ))}
        {/* Tooltip */}
        {hovered !== null && (() => {
          const d = data[hovered];
          const lines = [
            `Peak: ${sessionsLabel(d.peak)}`,
            `P90: ${sessionsLabel(d.p90)}`,
            `Mean: ${meanLabel(d.mean)}`,
            `Active ${d.activeMinutes.toLocaleString()} of ${d.minutes.toLocaleString()} min`,
          ];
          const tooltipW = Math.max(120, Math.max(...lines.map(l => l.length)) * 7 + 20);
          const tooltipH = 72;
          const cx = xAt(hovered) + slot / 2;
          const clampedX = Math.max(padding.left + tooltipW / 2, Math.min(width - padding.right - tooltipW / 2, cx));
          return (
            <g className={styles.tooltip} pointerEvents="none">
              <rect x={clampedX - tooltipW / 2} y={padding.top} width={tooltipW} height={tooltipH} className={styles.tooltipBg} />
              <text x={clampedX} y={padding.top + 12} textAnchor="middle" className={styles.tooltipTitle}>{formatTime(d.timestamp)}</text>
              {lines.map((line, i) => (
                <text key={line} x={clampedX} y={padding.top + 26 + i * 12} textAnchor="middle" className={styles.tooltipValue}>{line}</text>
              ))}
            </g>
          );
        })()}
      </svg>
      <div className={styles.chartLegend}>
        <div className={styles.legendItem}>
          <span className={styles.legendMeanSwatch} />
          <span>Mean</span>
        </div>
        <div className={styles.legendItem}>
          <span className={styles.legendP90Swatch} />
          <span>P90</span>
        </div>
        <div className={styles.legendItem}>
          <span className={styles.legendPeakSwatch} />
          <span>Peak</span>
        </div>
      </div>
    </div>
  );
}
